import React from "react";

import V2Table from "../../../v2/components/V2Table";
import V2Badge from "../../../v2/components/V2Badge";
import ChecklistPhotoPreview from "./ChecklistPhotoPreview";
import isHttpsUrl from "./isHttpsUrl";

import "./ChecklistMatrixTable.css";

const CHECKLIST_COLUMN_WIDTH = 290;
const RESPONSIBLE_COLUMN_WIDTH = 226;
const STORE_COLUMN_WIDTH = 110;
const PHOTOS_COLUMN_WIDTH = 96;
const PHOTOS_COLUMN_KEY = "__photos";

// Archived: set to true to show worker Call/WhatsApp links again.
const SHOW_RESPONDER_CONTACTS = false;

function ResponderContacts({ responder }) {
  if (!responder) return null;
  const whatsappLink = isHttpsUrl(responder.whatsappLink) ? responder.whatsappLink : null;
  if (!responder.mobile && !whatsappLink) return null;

  return (
    <div className="responsible-person-contacts">
      {responder.mobile && (
        <a className="contact-link contact-link-call" href={`tel:${responder.mobile}`}>
          {"Call ↗"}
        </a>
      )}
      {whatsappLink && (
        <a
          className="contact-link contact-link-whatsapp"
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          {"WhatsApp ↗"}
        </a>
      )}
    </div>
  );
}

/**
 * ChecklistMatrixTable — the item x store checklist matrix (Admin view).
 * Presentational: owns the checklist-specific column definitions and hands
 * the generic layout/scroll/sticky work to V2Table. All data comes in via
 * props — no data source (mock or live API) is hardcoded here, so callers
 * (currently just AdminMatrixView) own fetching/shaping the data.
 *
 * Props:
 * - stores: [{ id, nameLines: string[], managerName, submittedToHq }]
 * - items: [{ id, title, roleName, section, cellsByStore: { [storeId]: cell } }]
 *     `cell` is `{ status, submissionId?, response?, severity?, respondedBy?, imageUrls? }`.
 *     `imageUrls` holds the worker's photos (shown when `showPhotos`).
 *     `status` is 'done' | 'flagged' | 'pending' | 'not_submitted'. Items
 *     are expected pre-sorted in display order (grouped by section) —
 *     this component does not re-sort them.
 * - sectionSummary: [{ name, count }] — total item count per section, in
 *     the same section order the items appear in.
 * - activeStatuses: Set of statuses to filter by (shared with
 *     ChecklistStatusLegend) — empty/undefined means no filter.
 * - onCellClick?: ({ item, store, cell }) => void — a store cell is only
 *     clickable when its `cell.submissionId` exists (nothing to show for a
 *     pending/not-submitted cell yet); omit to make no cell clickable.
 * - onStoreHeaderClick?: (store) => void — opens rating/monthly-score for
 *     that store; omit to make store headers non-interactive.
 * - loading?: boolean — renders skeleton rows while the data is fetched.
 * - skeletonColumnCount?: number — store columns to sketch while loading.
 * - showPhotos?: boolean — adds a "Photos" column after the stores: a
 *     thumbnail of the item's first photo that opens the full-screen
 *     viewer. Store Manager view only (one store).
 *
 * Call/WhatsApp links (currently off, see SHOW_RESPONDER_CONTACTS) show
 * only once a cell carries `respondedBy`
 * ({ name, mobile, whatsappLink }).
 */
export default function ChecklistMatrixTable({
  stores,
  items,
  sectionSummary,
  activeStatuses,
  onCellClick,
  onStoreHeaderClick,
  loading = false,
  skeletonColumnCount,
  showPhotos = false,
}) {
  const hasFilter = Boolean(activeStatuses && activeStatuses.size > 0);

  // `respondedBy` only arrives once a store has completed the item; with
  // several stores, the row links to the first store (in column order) that has.
  const getResponder = (item) => stores.map((store) => item.cellsByStore[store.id]?.respondedBy).find(Boolean);

  const isItemVisible = (item) => {
    if (!hasFilter) return true;
    return stores.some((store) => activeStatuses.has(item.cellsByStore[store.id]?.status));
  };

  const frozenColumns = [
    {
      key: "checklist-item",
      width: CHECKLIST_COLUMN_WIDTH,
      renderHeader: () => (
        <div className="checklist-heading-cell">
          <p className="checklist-heading-title">Checklist item</p>
          <p className="checklist-heading-subtitle">
            {sectionSummary[0]?.name} {sectionSummary[0]?.count} items
          </p>
        </div>
      ),
      renderCell: (row) => (
        <div className="checklist-item-cell">
          <span className="checklist-item-number">{String(row.displayNumber).padStart(2, "0")}</span>
          <span className="checklist-item-title">{row.item.title}</span>
        </div>
      ),
    },
    {
      key: "responsible-role",
      width: RESPONSIBLE_COLUMN_WIDTH,
      renderHeader: () => (
        <div className="responsible-heading-cell">
          <p className="checklist-heading-title">Responsible role</p>
        </div>
      ),
      renderCell: (row) => (
        <div className="responsible-person-cell">
          <p className="responsible-person-role">{row.item.roleName}</p>
          {SHOW_RESPONDER_CONTACTS && <ResponderContacts responder={getResponder(row.item)} />}
        </div>
      ),
    },
  ];

  const groupColumns = stores.map((store) => ({
    key: store.id,
    width: STORE_COLUMN_WIDTH,
    renderHeader: () => {
      const content = (
        <>
          <div className="store-heading-name">
            {store.nameLines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
          <p className="store-heading-manager">SM: {store.managerName}</p>
          {store.submittedToHq !== undefined && (
            <p className={`store-heading-status ${store.submittedToHq ? "is-submitted" : "is-pending"}`}>
              {store.submittedToHq ? "✓ Submitted to HQ" : "In progress"}
            </p>
          )}
        </>
      );
      return onStoreHeaderClick ? (
        <button
          type="button"
          className="store-heading-cell store-heading-cell-button"
          onClick={() => onStoreHeaderClick(store)}
        >
          {content}
        </button>
      ) : (
        <div className="store-heading-cell">{content}</div>
      );
    },
    renderCell: (row) => {
      const cell = row.item.cellsByStore[store.id];
      const status = cell?.status;
      const badge = <V2Badge status={status} dimmed={hasFilter && !activeStatuses.has(status)} />;
      const clickable = Boolean(cell?.submissionId && onCellClick);
      return (
        <div className="store-status-cell">
          {clickable ? (
            <button
              type="button"
              className="store-status-cell-button"
              onClick={() => onCellClick({ item: row.item, store, cell })}
            >
              {badge}
            </button>
          ) : (
            badge
          )}
        </div>
      );
    },
  }));

  if (showPhotos) {
    groupColumns.push({
      key: PHOTOS_COLUMN_KEY,
      width: PHOTOS_COLUMN_WIDTH,
      renderHeader: () => (
        <div className="store-heading-cell">
          <p className="checklist-heading-title">Photos</p>
        </div>
      ),
      // One store in the Store Manager view; photos come from its cell.
      renderCell: (row) => (
        <div className="store-status-cell">
          <ChecklistPhotoPreview
            urls={stores.map((store) => row.item.cellsByStore[store.id]?.imageUrls || []).find((u) => u.length)}
            itemTitle={row.item.title}
          />
        </div>
      ),
    });
  }

  const visibleItems = items.filter(isItemVisible);
  const visibleCountBySection = {};
  visibleItems.forEach((item) => {
    visibleCountBySection[item.section] = (visibleCountBySection[item.section] || 0) + 1;
  });

  const rows = [];
  let lastSection = null;
  let displayNumber = 0;
  visibleItems.forEach((item) => {
    displayNumber += 1;

    if (item.section !== lastSection) {
      if (lastSection !== null) {
        const totalInSection = sectionSummary.find((s) => s.name === item.section)?.count ?? 0;
        const visibleInSection = visibleCountBySection[item.section];
        rows.push({
          key: `section-${item.section}`,
          type: "section",
          renderSection: () => (
            <>
              <span className="section-heading-name">{item.section}</span>
              <span className="section-heading-count">
                {hasFilter ? `${visibleInSection} of ${totalInSection}` : totalInSection} items
              </span>
            </>
          ),
        });
      }
      lastSection = item.section;
    }

    rows.push({ key: `item-${item.id}`, type: "data", item, displayNumber });
  });

  const footer = {
    label: `Store score · Done / ${items.length}`,
    renderCell: (col) => {
      if (col.key === PHOTOS_COLUMN_KEY) return null;
      const doneCount = items.filter((item) => item.cellsByStore[col.key]?.status === "done").length;
      return (
        <div className="store-status-cell">
          <span className="store-score">{`${doneCount}/${items.length}`}</span>
        </div>
      );
    },
  };

  return (
    <V2Table
      frozenColumns={frozenColumns}
      groupColumns={groupColumns}
      rows={rows}
      footer={footer}
      loading={loading}
      skeletonColumnCount={skeletonColumnCount}
      emptyMessage="No checklist items match the selected filter(s)."
    />
  );
}
